import { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { mapValues } from 'lodash-es';
import { Grid } from '../layout';
import { NumericTextField } from '../../../components/NumericTextField';

const attributes = {
  percentage: { min: 1, max: 100 },
  nitrogen: { min: 0, max: 100 },
  phosphorus: { min: 0, max: 100 },
  potassium: { min: 0, max: 100 },
  sulfur: { min: 0, max: 100 },
  density: { min: 1 },
};

export function ProductConfiguration({ product, onCancel, onSave }) {
  const [inputs, setInputs] = useState(product);

  return (
    <Dialog
      open
      sx={{
        '& .MuiFormHelperText-root.Mui-error': {
          position: 'absolute',
          top: 42,
        },
      }}
    >
      <form onSubmit={submit}>
        <DialogContent sx={{ height: product.custom ? undefined : 175, pb: 2 }}>
          <Box sx={{ display: 'flex', alignContent: 'flex-start' }}>
            <Typography variant="h5" sx={{ mt: 1, flex: 1 }}>
              {product.name}
            </Typography>
            <IconButton onClick={onCancel}>
              <CloseIcon />
            </IconButton>
          </Box>
          {product.custom ? buildCustomInputs() : buildStandardInputs()}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="contained" endIcon={<ChevronRight />} type="submit">
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );

  function submit(e) {
    e.preventDefault();
    const numericAttributes = mapValues(inputs, (v, k) =>
      attributes[k] ? Number(v) : v,
    );

    const { nitrogen, phosphorus, potassium, sulfur } = numericAttributes;
    const name = product.custom
      ? [nitrogen, phosphorus, potassium, sulfur].join('-')
      : product.name;

    onSave({
      ...product,
      ...numericAttributes,
      name,
    });
  }

  function buildCustomInputs() {
    return (
      <Grid columns={1}>
        <Typography sx={{ flex: 1 }} variant="subtitle2">
          Input Values
        </Typography>
        <Typography variant="subtitle2">
          *Percent of element per weight
        </Typography>
        <Grid columns={2} spacing={2} sx={{ mt: 1 }}>
          {textInput({ label: 'N', key: 'nitrogen', autoFocus: true })}
          {textInput({ label: 'P', key: 'phosphorus' })}
          {textInput({ label: 'K', key: 'potassium' })}
          {textInput({ label: 'Sulfur', key: 'sulfur' })}
          {textInput({ label: 'Density', key: 'density', allowDecimals: true })}
          {textInput({ label: 'Percent', key: 'percentage' })}
        </Grid>
      </Grid>
    );
  }

  function buildStandardInputs() {
    return (
      <>
        <Typography sx={{ mb: 2 }} variant="subtitle2">
          Input Percent of Mixture
        </Typography>
        {textInput({ label: 'Percent', key: 'percentage', autoFocus: true })}
      </>
    );
  }

  function textInput({ key, autoFocus, ...passThrough }) {
    return (
      <NumericTextField
        required
        ignoreNonNumericInput
        sx={{ width: product.custom ? '100%' : 380 }}
        size="small"
        variant="filled"
        value={inputs[key] === undefined ? '' : inputs[key]}
        InputProps={{ autoFocus }}
        InputLabelProps={{ shrink: true }}
        onChange={(event) => {
          setInputs((current) => ({ ...current, [key]: event.target.value }));
        }}
        {...attributes[key]}
        {...passThrough}
      />
    );
  }
}
